import { useEffect, useMemo, useState } from 'react';
import { data_from_server_post } from '@/shared/http/client';

/**
 * Body of the `evaluation` presence probe: the newest version, no comparison.
 * The endpoint answers `success: true` only when metrics exist, so the probe
 * tells results apart from "nothing yet", "in progress" and "failed".
 */
export const evaluationPresenceBody = (usecase) => ({
    client_id: usecase.client_id,
    llm_usecase_id: usecase._id,
    version: null,
    comparison_mode: false,
    compare_with_version: null,
    sub_task_type: null,
});

const probeUsecase = (usecase) =>
    new Promise((resolve) => {
        if (!usecase?._id || !usecase?.client_id) {
            resolve({ id: usecase?._id, ok: false });
            return;
        }
        data_from_server_post(
            'evaluation',
            evaluationPresenceBody(usecase),
            (result) => resolve({ id: usecase._id, ok: Boolean(result?.success) }),
            () => resolve({ id: usecase._id, ok: false })
        );
    });

/** Ready-signal keys (`${usecaseId}_${version}`) that belong to these usecases. */
const readySignatureFor = (usecases, evaluationResultsReady) => {
    const ids = usecases.map((u) => String(u?._id));
    return Object.keys(evaluationResultsReady || {})
        .filter((key) => ids.some((id) => key.startsWith(`${id}_`)))
        .sort()
        .join('|');
};

/**
 * Asks the backend whether each usecase has evaluation results and returns
 * `{ [usecaseId]: boolean }` — `{}` until the first answer, so callers that
 * gate on it stay off until results are confirmed.
 *
 * Re-probes when the usecase set changes, when a ready signal for one of them
 * arrives over the WebSocket (`evaluationResultsReady`, also set for failed
 * runs, which is why the signal alone never counts as results) and on
 * `realtimeResyncTick`, so a completion published while the socket was down
 * is picked up without a reload. Runs regardless of which tab is open.
 */
export function useEvaluationPresence({
    usecases = [],
    evaluationResultsReady = {},
    realtimeResyncTick = 0,
    enabled = true,
}) {
    const [presenceByUsecaseId, setPresenceByUsecaseId] = useState({});

    const usecaseKey = useMemo(
        () =>
            usecases
                .map((u) => `${u?._id}:${u?.client_id}`)
                .sort()
                .join('|'),
        [usecases]
    );
    const readySignature = useMemo(
        () => readySignatureFor(usecases, evaluationResultsReady),
        [usecases, evaluationResultsReady]
    );

    useEffect(() => {
        if (!enabled || usecases.length === 0) {
            setPresenceByUsecaseId({});
            return undefined;
        }

        let cancelled = false;
        Promise.all(usecases.map(probeUsecase)).then((rows) => {
            if (cancelled) return;
            const next = {};
            rows.forEach(({ id, ok }) => {
                if (id) next[String(id)] = ok;
            });
            setPresenceByUsecaseId(next);
        });

        return () => {
            cancelled = true;
        };
        // `usecases` is covered by `usecaseKey`; a new array with the same
        // ids must not re-probe.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, usecaseKey, readySignature, realtimeResyncTick]);

    return presenceByUsecaseId;
}
